import { useState, useMemo, useCallback, useEffect } from 'react';
import { ExistingCitationsTableRowData } from '../components/SelectCitations';
import {
  toggleStringSelection,
  toggleMultipleStringSelection,
  toggleStringSelectAll
} from '../utils/selectionHandlers';
import { useCitation } from '../Providers/CitationProvider';
import { Citation } from '../types';

// Constants
const DEFAULT_PRICE = 5;

/**
 * Transforms citation data from API format to table row format
 */
const transformCitationToRowData = (
  citation: Citation,
  index: number
): ExistingCitationsTableRowData => ({
  id: index + 1,
  citationSite: citation?.domain || '',
  businessName: citation?.nap?.name || '',
  address: citation?.nap?.address || '',
  zip: citation?.nap?.postcode || '',
  phoneNumber: citation?.nap?.phone || '',
  cost: citation?.cost ?? DEFAULT_PRICE,
  notes: []
});

/**
 * Extracts domains from citations that have mismatches
 */
const getDomainsWithMismatch = (citations: Citation[]): string[] =>
  citations
    .filter((citation) => citation.hasMismatch || citation.selected)
    .map((citation) => citation.domain);

/**
 * Calculates total price for selected citations
 */
const calculateSelectedTotalPrice = (
  tableData: ExistingCitationsTableRowData[],
  selectedDomains: string[]
): number =>
  tableData
    .filter((row) => selectedDomains.includes(row.citationSite))
    .reduce((total, row) => total + row.cost, 0);

export interface UseUpdateExistingCitationsReturn {
  // Data
  tableData: ExistingCitationsTableRowData[];
  selected: string[];
  totalPrice: number;

  // Sorting
  orderBy: string;
  order: 'asc' | 'desc';

  // Handlers
  handleSelect: (id: number | string) => void;
  handleSelectMultiple: (ids: (number | string)[], select: boolean) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveAll: () => void;
  handleRequestSort: (property: string) => void;

  // Utilities
  getRowId: (row: ExistingCitationsTableRowData) => string;
}

/**
 * Custom hook for managing existing citations selection, sorting, and pricing
 * Automatically selects citations with mismatches on initial load
 */
export const useUpdateExistingCitations =
  (): UseUpdateExistingCitationsReturn => {
    const { state: citationState, setSelectedExistingCitations } =
      useCitation();

    const selected = citationState.selectedExistingCitations;

    // Sorting state
    const [orderBy, setOrderBy] = useState<string>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    // Transform API data to table format
    const tableData = useMemo<ExistingCitationsTableRowData[]>(() => {
      if (!citationState.campaignDetails?.existingCitations?.citations) {
        return [];
      }
      return citationState.campaignDetails.existingCitations.citations.map(
        (citation, index) => transformCitationToRowData(citation, index)
      );
    }, [citationState.campaignDetails]);

    // Initialize selection with citations that have mismatches
    useEffect(() => {
      const citations =
        citationState.campaignDetails?.existingCitations?.citations;
      if (citations) {
        const domainsWithMismatch = getDomainsWithMismatch(citations);
        setSelectedExistingCitations(domainsWithMismatch);
      }
    }, [citationState.campaignDetails, setSelectedExistingCitations]);

    // Calculate total price for selected citations
    const totalPrice = useMemo(
      () => calculateSelectedTotalPrice(tableData, selected),
      [tableData, selected]
    );

    // Sort handler
    const handleRequestSort = useCallback(
      (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrderBy(property);
        setOrder(isAsc ? 'desc' : 'asc');
      },
      [orderBy, order]
    );

    // Selection handlers
    const handleSelect = useCallback(
      (id: number | string) => {
        setSelectedExistingCitations(toggleStringSelection(selected, id));
      },
      [selected, setSelectedExistingCitations]
    );

    const handleSelectMultiple = useCallback(
      (ids: (number | string)[], select: boolean) => {
        setSelectedExistingCitations(
          toggleMultipleStringSelection(selected, ids, select)
        );
      },
      [selected, setSelectedExistingCitations]
    );

    const handleSelectAll = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const allDomains = tableData.map((row) => row.citationSite);
        setSelectedExistingCitations(
          toggleStringSelectAll(allDomains, selected, event.target.checked)
        );
      },
      [tableData, selected, setSelectedExistingCitations]
    );

    const handleRemoveAll = useCallback(() => {
      setSelectedExistingCitations([]);
    }, [setSelectedExistingCitations]);

    // Utility function to get row ID
    const getRowId = useCallback(
      (row: ExistingCitationsTableRowData) => row.citationSite,
      []
    );

    return {
      tableData,
      selected,
      totalPrice,
      orderBy,
      order,
      handleSelect,
      handleSelectMultiple,
      handleSelectAll,
      handleRemoveAll,
      handleRequestSort,
      getRowId
    };
  };
